import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME, SVG } from "./attributes";

import { SharedSearch } from "@/components/shared";

import { SvgAttribute } from "@/shared/constants/constants";
import { HistoryTab, HistoryFiatCryptoTab } from "@/shared/constants/enums";
import { HistoryFilterRequest, Search } from "@/shared/constants/interfaces";
import { DepositWithdrawalHistoryResponse } from "@/shared/repository/modules/deposit-withdrawal/repo";
import { ApplicationHistoryResponse } from "@/shared/repository/modules/application/repo";

export interface HistoryHeaderTab {
  id: number;
  label: string;
  value: HistoryTab;
  acitve: boolean;
  isDisabled: boolean;
}

export interface HistoryFiatCryptoHeaderTab {
  id: number;
  label: string;
  value: HistoryFiatCryptoTab;
  acitve: boolean;
  isDisabled: boolean;
}

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SharedSearch,
  },
  props: {
    history: {
      type: <
        PropType<
          DepositWithdrawalHistoryResponse[] | ApplicationHistoryResponse[]
        >
      >Array,
      default: () => [],
    },
  },
  emits: {
    "update:history": null,
  },
  data() {
    return {
      isLoading: false,
      historyRepo: this.$projectServices.historyRepo,
      depositWithdrawalRepository:
        this.$projectServices.depositWithdrawalRepository,
      applicationRepository: this.$projectServices.applicationRepository,
      walletRepository: this.$projectServices.walletRepository,
    };
  },
  computed: {
    displayedTab(): HistoryTab {
      return this.historyRepo.tab;
    },

    displayedFiatCryptoTab(): HistoryFiatCryptoTab {
      return this.historyRepo.tabFiatCrypto;
    },

    displayedFilter(): HistoryFilterRequest {
      return this.historyRepo.filter;
    },

    displayedSearch(): Search {
      return this.historyRepo.search;
    },

    displayedTabs(): HistoryHeaderTab[] {
      return [
        {
          id: 0,
          label: "Пополнение/Вывод",
          value: HistoryTab.depositWithdrawal,
          acitve: this.displayedTab === HistoryTab.depositWithdrawal,
          isDisabled: this.isLoading,
        },
        {
          id: 1,
          label: "Заявки",
          value: HistoryTab.application,
          acitve: this.displayedTab === HistoryTab.application,
          isDisabled: this.isLoading,
        },
      ];
    },

    displayedFiatCryptoTabs(): HistoryFiatCryptoHeaderTab[] {
      return [
        {
          id: 0,
          label: "Fiat",
          value: HistoryFiatCryptoTab.fiat,
          acitve: this.displayedFiatCryptoTab === HistoryFiatCryptoTab.fiat,
          isDisabled: this.isLoading,
        },
        {
          id: 1,
          label: "Crypto",
          value: HistoryFiatCryptoTab.crypto,
          acitve: this.displayedFiatCryptoTab === HistoryFiatCryptoTab.crypto,
          isDisabled: this.isLoading,
        },
      ];
    },

    showFiatCryptoTabs(): boolean {
      return this.historyRepo.tab === HistoryTab.depositWithdrawal;
    },

    displayedIcons(): SvgAttribute {
      return SVG;
    },

    hasFilter(): boolean {
      const {
        amount_from,
        amount_to,
        date_from,
        date_to,
        status,
        currency_id,
        type,
        coin,
      } = this.displayedFilter;

      return (
        !!amount_from ||
        !!amount_to ||
        !!date_from ||
        !!date_to ||
        !!status ||
        !!currency_id ||
        !!type ||
        !!coin
      );
    },

    isFilterActive(): boolean {
      return this.historyRepo.isFilter && this.hasFilter;
    },
  },
  methods: {
    async handleTab({ value }: HistoryHeaderTab): Promise<void> {
      if (this.displayedTab !== value) {
        this.historyRepo.CLEAR_PAGE();
        this.historyRepo.CLEAR_TYPE();

        this.historyRepo.UPDATE_TAB(value);

        await this.handleFetch();
      }
    },

    async handleFiatCriptoTab({
      value,
    }: HistoryFiatCryptoHeaderTab): Promise<void> {
      if (this.displayedFiatCryptoTab !== value) {
        this.historyRepo.CLEAR_PAGE();
        this.historyRepo.CLEAR_TYPE();

        this.historyRepo.UPDATE_FIAT_CRYPTO_TAB(value);

        await this.handleFetch();
      }
    },

    handleFilter(): void {
      this.historyRepo.UPDATE_IS_FILTER(true);
    },

    async handleClearFilter(): Promise<void> {
      this.historyRepo.CLEAR_FILTER();

      await this.handleFetch();
    },

    handleSearch(value: string): void {
      this.historyRepo.UPDATE_SEARCH_VALUE(value);
    },

    async handleFetch(): Promise<void> {
      try {
        this.isLoading = true;
        this.$emit("update:history", []);

        let repository = null;
        if (this.displayedTab === HistoryTab.application) {
          repository = this.applicationRepository;
        }
        if (this.displayedTab === HistoryTab.depositWithdrawal) {
          if (this.displayedFiatCryptoTab === HistoryFiatCryptoTab.fiat) {
            repository = this.depositWithdrawalRepository;
          } else {
            repository = this.walletRepository;
          }
        }

        if (repository?.history) {
          const historyData = await repository.history(this.displayedFilter);
          this.$emit("update:history", historyData);
        }
      } catch (error) {
        console.error("Error fetching history:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
});
